// Modal de nova conta a receber. A CATEGORIA dirige o formulário (pedido
// Lucas 2026-07-08): seleciona o tipo de receita primeiro e abrem-se os
// campos adequados — rendimento pede só pagador; parcela de contrato pede
// vinculação (com aviso: o caminho recomendado é a aba Financeiro do
// contrato; criada por aqui entra na lista de reorganização do contrato).
// Posta fin_criar_conta_receber (contrato_id opcional no backend).
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { SelectCliente } from '../../components/SelectCliente';

const CATEGORIAS: Array<{ valor: string; rotulo: string; dica: string }> = [
  { valor: 'contrato', rotulo: '📄 Parcela de contrato (operacional)',
    dica: 'Caminho de exceção — o recomendado é criar na aba Financeiro do contrato.' },
  { valor: 'reembolso_contrato', rotulo: '↩️ Reembolso/estorno de contrato',
    dica: 'Entra no fechamento financeiro do contrato, mas fora da receita.' },
  { valor: 'receita_financeira', rotulo: '📈 Receita financeira (rendimento)',
    dica: 'Rendimentos de aplicação — o pagador é o banco.' },
  { valor: 'aporte_capital', rotulo: '💼 Aporte de capital', dica: '' },
  { valor: 'outras_receitas', rotulo: '➕ Outras receitas (estornos, ajustes)', dica: '' },
  { valor: 'transferencia_entre_contas', rotulo: '🔄 Transferência entre contas (não é receita)', dica: '' },
  { valor: 'emprestimo_captado', rotulo: '🏦 Empréstimo captado do banco (não é receita)',
    dica: 'O principal que o banco liberou é dívida. Cadastre o contrato em Financeiro › Empréstimos: só o juro das prestações vira despesa na DRE.' },
];
const COM_CONTRATO = ['contrato', 'reembolso_contrato'];
const COM_NF = ['contrato', 'outras_receitas'];

export function ModalNovaContaReceber({ onFechar, onCriou }: {
  onFechar: () => void;
  onCriou: () => void;
}) {
  const [form, setForm] = useState({
    tipo_receita: '',
    descricao: '',
    valor_previsto: '',
    data_vencimento: '',
    cliente_id: '',
    contrato_id: '',
    data_recebimento: '',
    data_competencia: '',
    descontos: '',
    descontos_motivo: '',
    status: 'pendente',
    nota_fiscal_numero: '',
    observacoes: '',
    // Lucas 2026-07-08: entrada manual de recebível contratual associa à
    // parcela PREVISTA do contrato (vira filho dela) em vez de linha solta.
    parcela_destino: '',
  });
  const [parcelasDoContrato, setParcelasDoContrato] = useState<Array<{
    id: string; descricao: string; valor_previsto: number; data_vencimento: string | null;
    status: string; parcela_pai_id?: string | null; recebido_efetivo?: number | null;
  }>>([]);
  const [contratos, setContratos] = useState<Array<{
    id: string; numero: string; titulo: string; contrato_pai_id?: string | null; cliente_id?: number;
  }>>([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  // A lista de clientes vem do SelectCliente (cache de 10 min compartilhado).

  // Contratos só quando a categoria pede (evita carregar à toa pra rendimento)
  const pedeContrato = COM_CONTRATO.includes(form.tipo_receita);
  useEffect(() => {
    if (!pedeContrato || contratos.length) return;
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_listar_contratos', incluir_aditivos: true },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setContratos(Array.isArray(d) ? d : []);
      } catch { /* dropdown vazio se falhar */ }
    })();
  }, [pedeContrato, contratos.length]);

  // Multi-CNPJ (2026-08-04): ORDENA em vez de filtrar. Escolher a usina como
  // tomador não pode esconder o contrato da CERILUZ — os do cliente vêm
  // primeiro, o resto continua acessível.
  const contratosDoCliente = (() => {
    if (!form.cliente_id) return contratos;
    const cliId = parseInt(form.cliente_id);
    const dele = contratos.filter(c => c.cliente_id === cliId);
    const resto = contratos.filter(c => c.cliente_id !== cliId);
    return [...dele, ...resto];
  })();
  const nDoCliente = form.cliente_id
    ? contratos.filter(c => c.cliente_id === parseInt(form.cliente_id)).length : 0;
  const categoria = CATEGORIAS.find(c => c.valor === form.tipo_receita);

  // Parcelas previstas do contrato escolhido (candidatas a receber a entrada).
  // Fora: canceladas e linhas que já são entradas (filhas).
  useEffect(() => {
    setParcelasDoContrato([]);
    if (form.tipo_receita !== 'contrato' || !form.contrato_id) return;
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'fin_listar_contas_receber', contrato_id: form.contrato_id },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        const rows = Array.isArray(d) ? d : [];
        setParcelasDoContrato(rows.filter((p: any) =>
          !p.parcela_pai_id && p.status !== 'cancelada'));
      } catch { /* select fica vazio */ }
    })();
  }, [form.tipo_receita, form.contrato_id]);
  const parcelaDestino = parcelasDoContrato.find(p => p.id === form.parcela_destino);

  async function salvar() {
    setErro('');
    if (!form.tipo_receita) { setErro('Escolha a categoria primeiro'); return; }
    const valorNum = parseFloat(form.valor_previsto);
    if (isNaN(valorNum) || valorNum <= 0) { setErro('Valor inválido'); return; }
    if (form.tipo_receita === 'contrato' && !form.contrato_id) {
      setErro('Parcela de contrato precisa do contrato vinculado — ou use outra categoria');
      return;
    }
    // Caminho recomendado: entrada DENTRO da parcela prevista escolhida.
    if (form.parcela_destino) {
      setSalvando(true);
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, {
          action: 'fin_registrar_entrada_parcela',
          parcela_id: form.parcela_destino,
          valor: valorNum,
          data_recebimento: form.data_recebimento || undefined,
          descricao: form.descricao.trim() || undefined,
        }, { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        if (d?.erro) { setErro(d.erro); return; }
        onCriou();
      } catch (e: any) {
        setErro(e.response?.data?.erro || e.response?.data?.error || e.message);
      } finally { setSalvando(false); }
      return;
    }
    if (!form.descricao.trim() || !form.valor_previsto || !form.data_vencimento) {
      setErro('Preencha descrição, valor bruto previsto e data de vencimento');
      return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_criar_conta_receber',
        tipo_receita: form.tipo_receita,
        descricao: form.descricao.trim(),
        valor_previsto: valorNum,
        data_vencimento: form.data_vencimento,
        cliente_id: form.cliente_id ? parseInt(form.cliente_id) : undefined,
        contrato_id: pedeContrato && form.contrato_id ? form.contrato_id : undefined,
        // Parcela de contrato criada por aqui entra na lista de reorganização
        criada_via_financeiro: form.tipo_receita === 'contrato' ? true : undefined,
        data_recebimento: form.data_recebimento || undefined,
        // FIN-7ajustes: competência editável (default no backend = recebimento||vencimento)
        data_competencia: form.data_competencia || undefined,
        descontos: form.descontos ? parseFloat(form.descontos) : undefined,
        descontos_motivo: form.descontos_motivo.trim() || undefined,
        status: form.status,
        nota_fiscal_numero: COM_NF.includes(form.tipo_receita)
          ? (form.nota_fiscal_numero.trim() || undefined) : undefined,
        observacoes: form.observacoes.trim() || undefined,
      }, { headers: { Authorization: token } });
      onCriou();
    } catch (e: any) {
      setErro(e.response?.data?.erro || e.response?.data?.error || e.message);
    } finally { setSalvando(false); }
  }

  const inp: CSSProperties = {
    width: '100%', padding: '7px 9px', fontSize: 13, border: '1px solid #ccc',
    borderRadius: 4, boxSizing: 'border-box',
  };
  const lbl: CSSProperties = {
    fontSize: 11, color: '#666', fontWeight: 600, marginBottom: 3, display: 'block',
  };

  // FIN-UI (2026-07-14): portalizado pro body (fixed dentro das Tabs do hub caía pra baixo).
  return createPortal(
    <div onClick={onFechar} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 8, maxWidth: 560, width: '100%',
        maxHeight: '92vh', overflow: 'auto', boxShadow: '0 12px 48px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '0.5px solid #e0e0e0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 15, color: '#020D7F' }}>+ Nova conta a receber</h3>
          <button onClick={onFechar} style={{ background: 'transparent', border: 'none',
            fontSize: 20, color: '#888', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={lbl}>Categoria *</label>
            <select value={form.tipo_receita}
              onChange={e => setForm({ ...form, tipo_receita: e.target.value, contrato_id: '', parcela_destino: '' })}
              style={inp} autoFocus>
              <option value="">— escolha a categoria do lançamento —</option>
              {CATEGORIAS.map(c => <option key={c.valor} value={c.valor}>{c.rotulo}</option>)}
            </select>
            {categoria?.dica && (
              <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>{categoria.dica}</div>
            )}
          </div>

          {form.tipo_receita === 'contrato' && !form.parcela_destino && (
            <div style={{ padding: '8px 12px', background: '#FFF8E8',
              border: '0.5px solid #E8C777', borderRadius: 5, fontSize: 11, color: '#7C5800' }}>
              ⚠ <strong>Recebível de contrato:</strong> associe abaixo a uma <strong>parcela
              prevista</strong> — o valor entra como entrada dentro dela. Sem associar, vira
              parcela nova avulsa e entra na <strong>lista de reorganização</strong> do
              contrato pra você encaixar no plano depois.
            </div>
          )}

          {form.tipo_receita && (<>
            <div>
              <label style={lbl}>{pedeContrato ? 'Faturar para' : 'Pagador'} (opcional)</label>
              {/* Trocar o tomador não zera mais o contrato: uma parcela pode
                  faturar pra usina e pertencer ao contrato da CERILUZ. */}
              <SelectCliente
                value={form.cliente_id ? parseInt(form.cliente_id) : null}
                onChange={v => setForm({ ...form, cliente_id: v == null ? '' : String(v) })}
                placeholder={`— sem ${pedeContrato ? 'cliente' : 'pagador'} —`}
              />
            </div>
            {pedeContrato && (
              <div>
                <label style={lbl}>
                  Contrato vinculado {form.tipo_receita === 'contrato' ? '*' : '(opcional)'}
                  {form.cliente_id && nDoCliente > 0 && (
                    <span style={{ color: '#888', fontWeight: 400 }}> — os {nDoCliente} do cliente acima vêm primeiro</span>
                  )}
                </label>
                <select value={form.contrato_id}
                  onChange={e => setForm({ ...form, contrato_id: e.target.value, parcela_destino: '' })} style={inp}>
                  <option value="">— escolha o contrato —</option>
                  {contratosDoCliente
                    .slice()
                    .sort((a, b) => {
                      // Contratos do cliente selecionado primeiro; dentro de cada
                      // bloco, pais antes de aditivos.
                      const cliId = form.cliente_id ? parseInt(form.cliente_id) : null;
                      if (cliId) {
                        const aDele = a.cliente_id === cliId, bDele = b.cliente_id === cliId;
                        if (aDele !== bDele) return aDele ? -1 : 1;
                      }
                      if (!a.contrato_pai_id && b.contrato_pai_id) return -1;
                      if (a.contrato_pai_id && !b.contrato_pai_id) return 1;
                      return a.numero.localeCompare(b.numero);
                    })
                    .map(c => (
                      <option key={c.id} value={c.id}>
                        {c.contrato_pai_id ? '  └─ ' : ''}
                        {c.numero}{c.titulo ? ` · ${c.titulo.slice(0, 60)}` : ''}
                      </option>
                    ))}
                </select>
              </div>
            )}
            {form.tipo_receita === 'contrato' && form.contrato_id && (
              <div>
                <label style={lbl}>Associar à parcela prevista (recomendado)</label>
                <select value={form.parcela_destino}
                  onChange={e => setForm({ ...form, parcela_destino: e.target.value })} style={inp}>
                  <option value="">— não associar (cria parcela nova avulsa) —</option>
                  {parcelasDoContrato.map(p => (
                    <option key={p.id} value={p.id}>
                      {(p.descricao || 'Parcela').slice(0, 55)}
                      {' · R$ ' + (Number(p.valor_previsto) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {p.data_vencimento ? ' · venc ' + String(p.data_vencimento).slice(0, 10).split('-').reverse().join('/') : ''}
                      {' · ' + p.status}
                    </option>
                  ))}
                </select>
                {parcelaDestino && (
                  <div style={{ marginTop: 6, padding: '8px 12px', background: '#E8F5EE',
                    border: '0.5px solid #B5D6BF', borderRadius: 5, fontSize: 11, color: '#0a7f4a' }}>
                    ✓ O valor será registrado como <strong>entrada dentro desta parcela</strong> —
                    o recebido efetivo dela passa a somar as entradas e o status é recalculado.
                  </div>
                )}
              </div>
            )}
            <div>
              <label style={lbl}>Descrição *</label>
              <input value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })}
                placeholder={pedeContrato ? 'ex: Medição 03 — complemento' : 'ex: Rendimento de aplicação — outubro'}
                style={inp} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={lbl}>{form.parcela_destino ? 'Valor da entrada (R$) *' : 'Valor Bruto Previsto (R$) *'}</label>
                <input type="number" step={0.01} min={0} value={form.valor_previsto}
                  onChange={e => setForm({ ...form, valor_previsto: e.target.value })}
                  placeholder="0,00" style={inp} />
              </div>
              {form.parcela_destino ? (
                <div>
                  <label style={lbl}>Data da entrada (padrão: hoje)</label>
                  <input type="date" value={form.data_recebimento}
                    onChange={e => setForm({ ...form, data_recebimento: e.target.value })} style={inp} />
                </div>
              ) : (
                <div>
                  <label style={lbl}>Data de Vencimento *</label>
                  <input type="date" value={form.data_vencimento}
                    onChange={e => setForm({ ...form, data_vencimento: e.target.value })} style={inp} />
                </div>
              )}
            </div>
            {!form.parcela_destino && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={lbl}>Data de Recebimento (se já recebido)</label>
                  <input type="date" value={form.data_recebimento}
                    onChange={e => setForm({ ...form, data_recebimento: e.target.value })} style={inp} />
                </div>
                <div>
                  <label style={lbl}>Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={inp}>
                    <option value="pendente">Pendente</option>
                    <option value="faturada">Faturada</option>
                    <option value="recebida">Recebida</option>
                  </select>
                </div>
              </div>
            )}
            {!form.parcela_destino && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={lbl}>Data de Competência (mês contábil)</label>
                  <input type="date" value={form.data_competencia}
                    onChange={e => setForm({ ...form, data_competencia: e.target.value })} style={inp} />
                  <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>Vazio = mês da emissão da NF; sem emissão, recebimento ou vencimento.</div>
                </div>
                <div>
                  <label style={lbl}>Descontos (R$) — glosa, saldo do cliente</label>
                  <input type="number" step={0.01} min={0} value={form.descontos}
                    onChange={e => setForm({ ...form, descontos: e.target.value })}
                    placeholder="0,00" style={inp} />
                </div>
              </div>
            )}
            {!form.parcela_destino && parseFloat(form.descontos) > 0 && (
              <div>
                <label style={lbl}>Motivo do desconto</label>
                <input value={form.descontos_motivo}
                  onChange={e => setForm({ ...form, descontos_motivo: e.target.value })}
                  placeholder="ex: glosa da medição / saldo de crédito do cliente" style={inp} />
              </div>
            )}
            {!form.parcela_destino && COM_NF.includes(form.tipo_receita) && (
              <div>
                <label style={lbl}>Nº da Nota Fiscal (opcional)</label>
                <input value={form.nota_fiscal_numero}
                  onChange={e => setForm({ ...form, nota_fiscal_numero: e.target.value })} style={inp} />
              </div>
            )}
            <div>
              <label style={lbl}>Observações</label>
              <textarea value={form.observacoes} rows={2}
                onChange={e => setForm({ ...form, observacoes: e.target.value })}
                style={{ ...inp, resize: 'vertical' }} />
            </div>
          </>)}

          {erro && <div style={{ color: '#B91626', fontSize: 12 }}>{erro}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={onFechar} style={{ padding: '8px 16px', fontSize: 13,
              background: '#fff', color: '#666', border: '1px solid #ccc', borderRadius: 5, cursor: 'pointer' }}>
              Cancelar
            </button>
            <button onClick={salvar} disabled={salvando || !form.tipo_receita} style={{ padding: '8px 16px', fontSize: 13,
              fontWeight: 600, background: (salvando || !form.tipo_receita) ? '#999' : '#020D7F', color: '#fff',
              border: 'none', borderRadius: 5, cursor: (salvando || !form.tipo_receita) ? 'default' : 'pointer' }}>
              {salvando ? 'Salvando...' : (form.parcela_destino ? 'Registrar entrada' : 'Criar')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
